import type { UpdateAuditQuestionResponseCommandInput } from '@amzn/gbsa-helios-typescript-client';

export interface AuditResponseSaveState {
  readonly pendingCount: number;
  readonly hasErrors: boolean;
  readonly failedQuestionIds: readonly string[];
  readonly latestError: Error | null;
}

export interface AuditResponseSaveOperation {
  readonly auditId: string;
  readonly questionId: string;
  readonly operationKey: string;
  readonly operationKeys: readonly string[];
  readonly sequence: number;
  readonly supersededOperationKeys: readonly string[];
}

export const AuditResponseSaveScope = {
  ParentAnswer: 'parent-answer',
  SubQuestionAnswer: 'sub-question-answer',
  BulkAnswer: 'bulk-answer',
  Comment: 'comment',
  NaReason: 'na-reason',
  Media: 'media',
  MediaUpload: 'media-upload',
  Response: 'response',
} as const;

export type AuditResponseSaveScopeValue =
  (typeof AuditResponseSaveScope)[keyof typeof AuditResponseSaveScope];

export function buildAuditResponseSaveOperationKey(
  questionId: string,
  scope: AuditResponseSaveScopeValue,
  subQuestionId?: string,
): string {
  return `${questionId}:${scope}:${subQuestionId ?? 'parent'}`;
}

interface SaveFailure {
  readonly questionId: string;
  readonly error: Error;
}

interface AuditSaveEntry {
  readonly pending: Map<number, AuditResponseSaveOperation>;
  readonly latestSequenceByKey: Map<string, number>;
  readonly failuresByKey: Map<string, SaveFailure>;
  readonly listeners: Set<() => void>;
  snapshot: AuditResponseSaveState;
}

const EMPTY_FAILED_QUESTION_IDS: readonly string[] = [];

export const EMPTY_AUDIT_RESPONSE_SAVE_STATE: AuditResponseSaveState = {
  pendingCount: 0,
  hasErrors: false,
  failedQuestionIds: EMPTY_FAILED_QUESTION_IDS,
  latestError: null,
};

const entries = new Map<string, AuditSaveEntry>();
let preparedOperations = new WeakMap<
  UpdateAuditQuestionResponseCommandInput,
  AuditResponseSaveOperation
>();
let nextSequence = 1;

function createEntry(): AuditSaveEntry {
  return {
    pending: new Map<number, AuditResponseSaveOperation>(),
    latestSequenceByKey: new Map<string, number>(),
    failuresByKey: new Map<string, SaveFailure>(),
    listeners: new Set<() => void>(),
    snapshot: EMPTY_AUDIT_RESPONSE_SAVE_STATE,
  };
}

function getOrCreateEntry(auditId: string): AuditSaveEntry {
  const existing = entries.get(auditId);
  if (existing) return existing;
  const created = createEntry();
  entries.set(auditId, created);
  return created;
}

function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  return new Error('A question response could not be saved.');
}

function publish(entry: AuditSaveEntry): void {
  const failures: SaveFailure[] = Array.from(entry.failuresByKey.values());
  const failedQuestionIds: string[] = [];
  for (const failure of failures) {
    if (!failedQuestionIds.includes(failure.questionId)) {
      failedQuestionIds.push(failure.questionId);
    }
  }
  entry.snapshot = {
    pendingCount: entry.pending.size,
    hasErrors: failures.length > 0,
    failedQuestionIds,
    latestError: failures.at(-1)?.error ?? null,
  };
  entry.listeners.forEach((listener: () => void) => listener());
}

function deleteEntryIfUnused(auditId: string, entry: AuditSaveEntry): void {
  if (
    entry.pending.size === 0 &&
    entry.failuresByKey.size === 0 &&
    entry.listeners.size === 0
  ) {
    entries.delete(auditId);
  }
}

function resolveSupersededOperationKeys(
  entry: AuditSaveEntry,
  questionId: string,
  explicitKeys: readonly string[],
  scopes: readonly AuditResponseSaveScopeValue[],
): readonly string[] {
  const resolved = new Set<string>(explicitKeys);
  const candidates: string[] = [];
  entry.latestSequenceByKey.forEach((_sequence: number, key: string) => {
    candidates.push(key);
  });
  entry.failuresByKey.forEach((_failure: SaveFailure, key: string) => {
    candidates.push(key);
  });
  for (const scope of scopes) {
    const prefix = `${questionId}:${scope}:`;
    for (const candidate of candidates) {
      if (candidate.startsWith(prefix)) resolved.add(candidate);
    }
  }
  const resolvedKeys: string[] = [];
  resolved.forEach((key: string) => resolvedKeys.push(key));
  return resolvedKeys;
}

/**
 * Registers one response-changing operation before optimistic cache writes run.
 * Reusing an operation key represents a retry/latest intent and clears the
 * previous failure for that key while the retry is pending.
 */
export function beginAuditResponseSave(
  auditId: string,
  questionId: string,
  operationKey = questionId,
  supersededOperationKeys: readonly string[] = [],
  supersededOperationScopes: readonly AuditResponseSaveScopeValue[] = [],
  additionalOperationKeys: readonly string[] = [],
): AuditResponseSaveOperation {
  const entry = getOrCreateEntry(auditId);
  const operationKeys: string[] = [operationKey];
  for (const additionalKey of additionalOperationKeys) {
    if (!operationKeys.includes(additionalKey)) operationKeys.push(additionalKey);
  }
  const resolvedSupersededKeys = resolveSupersededOperationKeys(
    entry,
    questionId,
    supersededOperationKeys,
    supersededOperationScopes,
  );
  const operation = {
    auditId,
    questionId,
    operationKey,
    operationKeys,
    sequence: nextSequence++,
    supersededOperationKeys: resolvedSupersededKeys,
  };
  entry.pending.set(operation.sequence, operation);
  for (const ownedKey of operationKeys) {
    entry.latestSequenceByKey.set(ownedKey, operation.sequence);
    entry.failuresByKey.delete(ownedKey);
  }
  for (const supersededKey of resolvedSupersededKeys) {
    entry.latestSequenceByKey.set(supersededKey, operation.sequence);
  }
  publish(entry);
  return operation;
}

/** Registers synchronously and attaches a non-wire operation token to the input. */
export function prepareAuditResponseSave(
  input: UpdateAuditQuestionResponseCommandInput,
  operationKey: string,
  supersededOperationKeys: readonly string[] = [],
  supersededOperationScopes: readonly AuditResponseSaveScopeValue[] = [],
  additionalOperationKeys: readonly string[] = [],
): AuditResponseSaveOperation {
  const existing = preparedOperations.get(input);
  const existingEntry = existing ? entries.get(existing.auditId) : undefined;
  if (existing && existingEntry?.pending.has(existing.sequence)) return existing;
  const operation = beginAuditResponseSave(
    input.auditId,
    input.questionId,
    operationKey,
    supersededOperationKeys,
    supersededOperationScopes,
    additionalOperationKeys,
  );
  preparedOperations.set(input, operation);
  return operation;
}

export function getPreparedAuditResponseSave(
  input: UpdateAuditQuestionResponseCommandInput,
): AuditResponseSaveOperation | undefined {
  return preparedOperations.get(input);
}

/**
 * Settles a registered save. Only the latest intent for an operation key may
 * change its failure state; stale completions still decrement pending count.
 */
export function settleAuditResponseSave(
  operation: AuditResponseSaveOperation | undefined,
  error: unknown = null,
): void {
  if (!operation) return;
  const entry = entries.get(operation.auditId);
  if (!entry?.pending.delete(operation.sequence)) return;

  const succeeded = error === null || error === undefined;
  for (const ownedKey of operation.operationKeys) {
    const isLatestIntent = entry.latestSequenceByKey.get(ownedKey) === operation.sequence;
    if (!isLatestIntent) continue;
    entry.latestSequenceByKey.delete(ownedKey);
    if (succeeded) {
      entry.failuresByKey.delete(ownedKey);
    } else {
      entry.failuresByKey.set(ownedKey, {
        questionId: operation.questionId,
        error: toError(error),
      });
    }
  }
  for (const supersededKey of operation.supersededOperationKeys) {
    if (entry.latestSequenceByKey.get(supersededKey) !== operation.sequence) continue;
    entry.latestSequenceByKey.delete(supersededKey);
    if (succeeded) entry.failuresByKey.delete(supersededKey);
  }
  publish(entry);
  deleteEntryIfUnused(operation.auditId, entry);
}

export function getAuditResponseSaveState(auditId: string): AuditResponseSaveState {
  return entries.get(auditId)?.snapshot ?? EMPTY_AUDIT_RESPONSE_SAVE_STATE;
}

export function subscribeAuditResponseSaveState(
  auditId: string,
  listener: () => void,
): () => void {
  if (!auditId) return () => undefined;
  const entry = getOrCreateEntry(auditId);
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
    deleteEntryIfUnused(auditId, entry);
  };
}

function buildPersistenceError(state: AuditResponseSaveState): Error {
  const questionList = state.failedQuestionIds.join(', ');
  const suffix = questionList ? ` Questions: ${questionList}.` : '';
  return new Error(
    `${state.latestError?.message ?? 'One or more question responses failed to save.'}${suffix}`,
  );
}

/** Waits for every registered save for an audit, including unmounted questions. */
export function drainAuditResponseSaves(auditId: string): Promise<void> {
  const checkState = (): Error | null | undefined => {
    const state = getAuditResponseSaveState(auditId);
    if (state.pendingCount > 0) return undefined;
    return state.hasErrors ? buildPersistenceError(state) : null;
  };

  const initial = checkState();
  if (initial === null) return Promise.resolve();
  if (initial instanceof Error) return Promise.reject(initial);

  return new Promise<void>((resolve, reject) => {
    let unsubscribe = (): void => undefined;
    const check = (): void => {
      const result = checkState();
      if (result === undefined) return;
      unsubscribe();
      if (result instanceof Error) reject(result);
      else resolve();
    };
    unsubscribe = subscribeAuditResponseSaveState(auditId, check);
    check();
  });
}

/** Test-only reset for the module-level registry. */
export function resetAuditResponseSaveCoordinator(): void {
  entries.clear();
  preparedOperations = new WeakMap<
    UpdateAuditQuestionResponseCommandInput,
    AuditResponseSaveOperation
  >();
  nextSequence = 1;
}
